import { zodResolver } from "@hookform/resolvers/zod";
import { SubmitHandler, useForm } from "react-hook-form";
import PhoneInput from "react-phone-input-2";

import "react-phone-input-2/lib/style.css";

import { z } from "zod";

import { HipaaLogo } from "~/icons";
import { phoneSchema } from "~/schemas/SignUp/Phone";
import { SignUpFormProps } from "~/shared/utils/types";
import { useSignUpStore } from "~/stores";
import { tw } from "~/utils";
import { ActionButtons } from "../ActionButtons";
import { ErrorMessage } from "../ErrorMessage";
import { FormContentContainer } from "../FormContentContainer";

export const PhoneForm = ({
  onPrev,
  onNext,
  stepId = "phone",
}: SignUpFormProps & { stepId?: string }) => {
  const { patient, setPatientField } = useSignUpStore();
  const { phone } = patient;

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<z.infer<typeof phoneSchema>>({
    mode: "onBlur",
    resolver: zodResolver(phoneSchema),
    defaultValues: {
      phone: phone ?? "",
      privacyPolicy: false,
    },
  });

  const phoneValue = watch("phone");

  const onSubmit: SubmitHandler<z.infer<typeof phoneSchema>> = (data) => {
    setPatientField("phone", data.phone);
    onNext();
  };

  const handlePrev = () => {
    setPatientField("phone", "");
    onPrev();
  };

  const handlePhoneChange = (
    value: string,
    country: {} | { dialCode?: string },
  ) => {
    if (
      typeof country === "object" &&
      "dialCode" in country &&
      country.dialCode
    ) {
      const dialCode = country.dialCode;
      const numberWithoutPrefix = value.replace(`+${dialCode}`, "");
      setValue("phone", numberWithoutPrefix);
    } else {
      setValue("phone", value);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="rounded-lg shadow-xl">
      <FormContentContainer stepId={stepId}>
        <HipaaLogo className="size-20 self-center" />
        <div>
          <h1 className="font-display text-2xl">
            What is the best phone number to reach you at?
          </h1>
        </div>
        <div className={tw("flex w-full flex-col gap-5", !errors && "mb-8")}>
          <PhoneInput
            country={"us"}
            value={phoneValue}
            onChange={(value, country) => handlePhoneChange(value, country)}
            inputProps={{
              id: "phone",
              required: true,
              "data-testid": "phone-input",
              ...register("phone"),
            }}
            containerClass="w-full"
            inputClass="!w-full !h-12"
            buttonClass="!bg-white !border-r-0"
          />
          <div className="flex items-start">
            <input
              type="checkbox"
              id="privacyPolicy"
              className="mt-1 size-4 cursor-pointer rounded"
              {...register("privacyPolicy")}
            />
            <label
              htmlFor="privacyPolicy"
              className="ml-2 text-sm font-medium text-gray-900"
            >
              By checking this box, you agree to receive SMS text messages from
              Sarah Lynn Nutrition, you may reply STOP to opt-out at any time,
              reply HELP for assistance, Messages and data rates may apply,
              messaging frequency may vary, this is our
              <a
                className="ml-1 cursor-pointer text-gray-500"
                target="_blank"
                href="https://sarahlynnnutrition.com/privacy-policy/"
              >
                privacy policy
              </a>
              .
            </label>
          </div>
        </div>
        {errors.phone && <ErrorMessage message={errors.phone.message ?? ""} />}
        {errors.privacyPolicy && (
          <ErrorMessage message={errors.privacyPolicy.message ?? ""} />
        )}
      </FormContentContainer>
      <ActionButtons onPrev={handlePrev} />
    </form>
  );
};
